import { Container, Graphics } from 'pixi.js'
import type { GridCoord } from '$lib/types'
import type { RenderableBuilding } from '../hit-test'
import { colours } from '../colours'

export interface InvalidHighlight {
  ids: ReadonlySet<number>
}

export class OverlayLayer {
  readonly container: Container = new Container()
  private readonly invalid: Graphics = new Graphics()
  private readonly hover: Graphics = new Graphics()
  private readonly selection: Graphics = new Graphics()
  private readonly storePreview: Graphics = new Graphics()
  private readonly strokePreview: Graphics = new Graphics()
  private readonly ghost: Graphics = new Graphics()

  constructor(private cellSize: number) {
    this.container.label = 'overlay-layer'
    // Order matters: invalid tint sits below hover/selection so the latter
    // still read clearly on a flagged building. Store preview (the red
    // tint applied to buildings caught in a sweep) sits above selection
    // but below stroke preview / ghost so a sweep that overlaps a drag
    // ghost (in practice impossible given dispatcher gating) can't hide
    // the in-flight gesture.
    this.container.addChild(this.invalid)
    this.container.addChild(this.hover)
    this.container.addChild(this.selection)
    this.container.addChild(this.storePreview)
    this.container.addChild(this.strokePreview)
    this.container.addChild(this.ghost)
  }

  setCellSize(cellSize: number): void {
    this.cellSize = cellSize
  }

  drawHover(b: RenderableBuilding | null): void {
    this.hover.clear()
    if (!b) return
    const c = this.cellSize
    this.hover
      .rect(b.building.x * c, b.building.y * c, b.width * c, b.height * c)
      .stroke({ width: 1, color: colours.hoverOutline(), alpha: 0.9 })
  }

  drawSelection(b: RenderableBuilding | null): void {
    this.selection.clear()
    if (!b) return
    const c = this.cellSize
    this.selection
      .rect(
        b.building.x * c + 1,
        b.building.y * c + 1,
        b.width * c - 2,
        b.height * c - 2,
      )
      .stroke({ width: 2, color: colours.selectionOutline() })
  }

  /**
   * Red overlay over every building flagged by the validator. Drawn as a
   * translucent fill plus a hard border so the colour reads even when the
   * underlying building shares the red palette (e.g. military).
   */
  drawInvalid(buildings: RenderableBuilding[], invalidIds: ReadonlySet<number>): void {
    this.invalid.clear()
    if (invalidIds.size === 0) return
    const c = this.cellSize
    const errColour = colours.validationError()
    for (const b of buildings) {
      if (!invalidIds.has(b.building.id)) continue
      this.invalid
        .rect(b.building.x * c, b.building.y * c, b.width * c, b.height * c)
        .fill({ color: errColour, alpha: 0.25 })
      this.invalid
        .rect(b.building.x * c, b.building.y * c, b.width * c, b.height * c)
        .stroke({ width: 2, color: errColour, alpha: 0.9 })
    }
  }

  /**
   * Footprint preview for an active drag or placement. `gridX`/`gridY` are
   * the snapped top-left tile. `valid` flips the colour to red when the
   * preview position would land in an overlap or outside the unlocked area.
   */
  drawGhost(
    gridX: number,
    gridY: number,
    width: number,
    height: number,
    valid: boolean = true,
  ): void {
    this.ghost.clear()
    const c = this.cellSize
    const fill = valid ? colours.ghostFill() : colours.validationError()
    const stroke = valid ? colours.ghostStroke() : colours.validationError()
    this.ghost
      .rect(gridX * c, gridY * c, width * c, height * c)
      .fill({ color: fill, alpha: 0.25 })
    this.ghost
      .rect(gridX * c, gridY * c, width * c, height * c)
      .stroke({ width: 2, color: stroke, alpha: 0.9 })
  }

  clearGhost(): void {
    this.ghost.clear()
  }

  /**
   * Render translucent road squares for the road tool's paint stroke.
   * Replaces (rather than adds to) the previous stroke each call, so the
   * road handler can pass the full stroke array on every pointermove
   * without bookkeeping per-cell adds.
   */
  drawStrokePreview(squares: ReadonlyArray<GridCoord>): void {
    this.strokePreview.clear()
    if (squares.length === 0) return
    const c = this.cellSize
    const colour = colours.roadPaintPreview()
    for (const sq of squares) {
      this.strokePreview
        .rect(sq.x * c, sq.y * c, c, c)
        .fill({ color: colour, alpha: 0.5 })
      this.strokePreview
        .rect(sq.x * c, sq.y * c, c, c)
        .stroke({ width: 1, color: colour, alpha: 0.9 })
    }
  }

  clearStrokePreview(): void {
    this.strokePreview.clear()
  }

  /**
   * Highlight a set of placed buildings as "marked for store" — used by
   * the store-tool sweep so the player can see what's about to be sent
   * to inventory before they release the mouse. Replaces the previous
   * mark set on every call.
   */
  drawStorePreview(
    ids: ReadonlySet<number>,
    buildings: ReadonlyArray<RenderableBuilding>,
  ): void {
    this.storePreview.clear()
    if (ids.size === 0) return
    const c = this.cellSize
    const colour = colours.validationError()
    for (const b of buildings) {
      if (!ids.has(b.building.id)) continue
      this.storePreview
        .rect(b.building.x * c, b.building.y * c, b.width * c, b.height * c)
        .fill({ color: colour, alpha: 0.4 })
      this.storePreview
        .rect(b.building.x * c, b.building.y * c, b.width * c, b.height * c)
        .stroke({ width: 2, color: colour, alpha: 0.95 })
    }
  }

  clearStorePreview(): void {
    this.storePreview.clear()
  }

  destroy(): void {
    this.container.destroy({ children: true })
  }
}
